import React, { useRef, useState } from "react";
import { Box, Button, IconButton, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import DeleteIcon from "@mui/icons-material/Delete";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import CircularProgress from "@mui/material/CircularProgress";

const VIDEO_MAX_SIZE = 15 * 1024 * 1024; // 15MB
const ALLOWED_EXTENSIONS = [".mp4", ".webp"];
const ALLOWED_MIME_TYPES = ["video/mp4", "image/webp"];

const useStyles = makeStyles(() => ({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  actions: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  error: {
    fontSize: 13,
    color: "#d32f2f",
    marginTop: 4,
    padding: "8px 12px",
    backgroundColor: "#ffebee",
    borderRadius: 6,
    border: "1px solid #ffcdd2",
  },
}));

interface VideoUploaderProps {
  onUpload: (file: File) => Promise<void>;
  onDelete: () => Promise<void>;
  hasVideo: boolean;
  metadata?: {
    container?: string;
    fileSize?: number;
  };
  disabled?: boolean;
  showDelete?: boolean;
}

const VideoUploader = ({
  onUpload,
  onDelete,
  hasVideo,
  metadata,
  disabled = false,
  showDelete = true,
}: VideoUploaderProps) => {
  const styles = useStyles();
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validateFile = (file: File): string | null => {
    const ext = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      return "Please ensure your file is an .mp4 or webP (H.264) under 15MB";
    }
    if (!ALLOWED_MIME_TYPES.includes(file.type)) {
      return "Please ensure your file is an .mp4 or webP (H.264) under 15MB";
    }
    if (file.size > VIDEO_MAX_SIZE) {
      return "Please ensure your file is an .mp4 or webP (H.264) under 15MB";
    }
    return null;
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (inputRef.current) inputRef.current.value = "";
    if (!file) return;

    setError(null);
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setUploading(true);
      await onUpload(file);
    } catch (err: any) {
      setError(err?.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setUploading(true);
      setError(null);
      await onDelete();
    } catch (err: any) {
      setError(err?.message || "Delete failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box className={styles.container}>
      <Box className={styles.actions}>
        <input
          ref={inputRef}
          type="file"
          accept=".mp4,.webp"
          style={{ display: "none" }}
          onChange={handleFileChange}
        />
        <Button
          variant={hasVideo ? "outlined" : "contained"}
          size="small"
          startIcon={
            uploading ? (
              <CircularProgress size={16} />
            ) : hasVideo ? (
              <AutorenewIcon />
            ) : (
              <UploadFileIcon />
            )
          }
          onClick={() => inputRef.current?.click()}
          disabled={disabled || uploading}
          sx={
            !hasVideo
              ? {
                  backgroundColor: "#00796b",
                  "&:hover": { backgroundColor: "#004d40" },
                }
              : {}
          }
        >
          {hasVideo ? "Replace Video" : "Upload Video"}
        </Button>
        {hasVideo && showDelete && (
          <IconButton
            size="small"
            color="error"
            onClick={handleDelete}
            disabled={disabled || uploading}
            title="Delete video"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
      {error && (
        <Typography className={styles.error}>
          Upload failed: This video does not meet the required specifications.
          <br />
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default VideoUploader;
